import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { setAccessTokenGetter as setSessionTokenGetter } from '@api/services/sessionService'
import { setAccessTokenGetter as setV1TokenGetter } from '@api/v1/client'
import { embedTokenGetter, initEmbedAuth, getEmbedToken } from './embedAuth'
import EmbedReportView from './EmbedReportView'
import EmbedReportsList from './EmbedReportsList'
import LoadingSpinner from '@components/common/LoadingSpinner'

// Embed-mode app — no Auth0Provider, no Layout chrome.
// Token is supplied by the parent window via postMessage. See ./embedAuth.js.
//
// Routes:
//   /embed/reports                 — catalog (for IQ "Browse all" panel)
//   /embed/reports/:reportId       — single report

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error?.response?.status === 401) return false
        return failureCount < 1
      },
      staleTime: 5 * 60 * 1000,
    },
  },
})

/*
 * TODO(spec 094) — Pendo is NOT initialized in embed mode, so anyone reading
 * reports through the IQ iframe is UNCOUNTED. This is an open product decision,
 * not an oversight: is `/embed/*` in scope for the Report Center beta metrics?
 *
 * Left unwired deliberately rather than guessed, because embed is not simply
 * "App.jsx without Auth0" and each difference changes the answer:
 *
 *   - Identity WOULD work. `sessionService`'s getter is repointed at
 *     `embedTokenGetter` below, so `resolvePendoIdentity()` would decode the
 *     parent's token and return the same `{tenant, cbUserId}` shape. Note it
 *     must come from the `cbsite` claim — `getTenant()` reads the iframe's own
 *     hostname and returns nothing useful here.
 *   - The parent can REPLACE the token at any time (embedAuth.js, protocol
 *     step 4). An identity captured once at mount could then be attributing a
 *     different user's activity, which is worse than not counting them.
 *   - Third-party-iframe storage is partitioned, so Pendo's own visitor cookie
 *     may not persist and one user could appear as many.
 *   - Counting the same human twice — once in cb2, once through IQ — would
 *     inflate the per-firm user counts the beta is being judged on.
 *
 * Whoever picks this up: decide the double-count question FIRST, then re-derive
 * identity on every token replacement rather than once at mount.
 */
const EmbedApp = () => {
  const [tokenReady, setTokenReady] = useState(!!getEmbedToken())

  useEffect(() => {
    setSessionTokenGetter(embedTokenGetter)
    setV1TokenGetter(embedTokenGetter)
    initEmbedAuth()

    if (getEmbedToken()) return
    let cancelled = false
    embedTokenGetter()
      .then(() => { if (!cancelled) setTokenReady(true) })
      .catch(() => { /* leave the spinner up; the parent should retry */ })
    return () => { cancelled = true }
  }, [])

  if (!tokenReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner message="Connecting…" />
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/embed/reports" element={<EmbedReportsList />} />
          <Route path="/embed/reports/:reportId" element={<EmbedReportView />} />
          <Route path="*" element={<Navigate to="/embed/reports" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default EmbedApp
